import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import AnnouncementBanner from './AnnouncementBanner';
import AnnouncementPopup from './AnnouncementPopup';
import AnnouncementCard from './AnnouncementCard';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AnnouncementsContainer = ({ showCards = false }) => {
  const [announcements, setAnnouncements] = useState({
    banners: [],
    popups: [],
    cards: []
  });
  const [dismissedIds, setDismissedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(true);
  const location = useLocation();
  
  // Pages where we should NOT show popups (auth pages except homepage)
  const noPopupPages = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
  const isAuthPage = noPopupPages.some(page => location.pathname.startsWith(page));
  const isHomePage = location.pathname === '/';

  const fetchAnnouncements = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      let response;
      if (token) {
        // Logged in user - get personalized announcements
        response = await axios.get(`${API_URL}/api/announcements/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Not logged in - get public announcements
        response = await axios.get(`${API_URL}/api/announcements/public`);
      }
      
      setAnnouncements({
        banners: response.data.banners || [],
        popups: response.data.popups || [],
        cards: response.data.cards || []
      });
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load dismissed announcements from sessionStorage
    const dismissed = sessionStorage.getItem('dismissedAnnouncements');
    if (dismissed) {
      setDismissedIds(JSON.parse(dismissed));
    }
    
    // Initial fetch
    fetchAnnouncements();
    
    // Listen for token changes (login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        // User logged in or out - refetch announcements
        fetchAnnouncements();
        setShowPopup(true);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check for token periodically (for same-tab login)
    const intervalId = setInterval(() => {
      const token = localStorage.getItem('token');
      const hasAnnouncements = announcements.banners.length > 0 || announcements.popups.length > 0 || announcements.cards.length > 0;
      
      // Refetch if logged in and no announcements, or if token state changed
      if (token && !hasAnnouncements) {
        fetchAnnouncements();
      }
    }, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [fetchAnnouncements, announcements.banners.length, announcements.popups.length, announcements.cards.length]);

  const handleDismiss = (announcementId) => {
    const newDismissed = [...dismissedIds, announcementId];
    setDismissedIds(newDismissed);
    sessionStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
  };

  const handlePopupClose = (announcementId) => {
    handleDismiss(announcementId);
    setShowPopup(false);
  };

  // Filter out dismissed announcements
  const activeBanners = announcements.banners.filter(a => !dismissedIds.includes(a.id));
  const activePopups = announcements.popups.filter(a => !dismissedIds.includes(a.id));
  const activeCards = announcements.cards.filter(a => !dismissedIds.includes(a.id));

  // Get the first popup to show (only show one at a time)
  // Don't show popups on auth pages
  const popupToShow = activePopups.length > 0 && showPopup && !isAuthPage ? activePopups[0] : null;

  if (loading) return null;

  return (
    <>
      {/* Banners - shown at top (but not on auth pages) */}
      {!isAuthPage && activeBanners.map(banner => (
        <AnnouncementBanner
          key={banner.id}
          announcement={banner}
          onDismiss={handleDismiss}
        />
      ))}

      {/* Popup - shown as modal */}
      {popupToShow && (
        <AnnouncementPopup
          announcement={popupToShow}
          onClose={handlePopupClose}
        />
      )}

      {/* Cards - rendered inline where AnnouncementsContainer is placed */}
      {showCards && activeCards.length > 0 && (
        <div className="space-y-3">
          {activeCards.map(card => (
            <AnnouncementCard
              key={card.id}
              announcement={card}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default AnnouncementsContainer;
